import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UploadZone } from "@/components/UploadZone";
import { ClipCard } from "@/components/ClipCard";
import { TemplateManager } from "@/components/TemplateManager";
import { Billing } from "@/components/Billing";
import { ClipAssistantPanel } from "@/components/ClipAssistantPanel";
import { BatchDownload } from "@/components/BatchDownload";
import { ManualWorkspace } from "@/components/ManualWorkspace";
import { CreatorWorkspace } from "@/components/CreatorWorkspace";
import { AccountSettings } from "@/components/AccountSettings";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Film, Layers, Flame, Zap, Clapperboard } from "lucide-react";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-surface-800 bg-panel p-5">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-4 ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-display text-3xl font-black">{value}</p>
      <p className="text-xs uppercase tracking-widest text-copy-500 mt-1">{label}</p>
    </div>
  );
}

export function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [clips, setClips] = useState([]);
  const [tab, setTab] = useState("overview");
  const [manualBusy, setManualBusy] = useState(false);
  const [studioBusy, setStudioBusy] = useState(false);
  const [projects, setProjects] = useState([]), [downloadProject, setDownloadProject] = useState('');
  const selectedProject = projects.find((project) => project.id === downloadProject);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  const refresh = () => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/clips").then(({ data }) => setClips(data)).catch(() => {});
    api.get('/projects', { params: { summary: true } }).then(({ data }) => setProjects(data.filter((project) => project.num_clips > 0))).catch(() => {});
  };
  useEffect(() => { if (user) refresh(); }, [user]);

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 px-5 sm:px-8 pb-20">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-copy-500 mb-2">Dashboard</p>
          <h1 className="font-display text-4xl font-black tracking-tighter">Hey {user.name} 👋</h1>
        </div>

        <Tabs value={tab} onValueChange={(next) => { if ((!manualBusy || next === 'manual') && (!studioBusy || next === 'studio')) setTab(next); }}>
          <TabsList className="bg-surface-900 border border-surface-800 rounded-full p-1 mb-8 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full data-[state=active]:bg-cyan-500 data-[state=active]:text-black">Overview</TabsTrigger>
            <TabsTrigger value="create" data-testid="tab-create" className="rounded-full data-[state=active]:bg-cyan-500 data-[state=active]:text-black">Create clips</TabsTrigger>
            <TabsTrigger value="assistant" data-testid="tab-assistant" className="rounded-full data-[state=active]:bg-cyan-500 data-[state=active]:text-black">AI specialist</TabsTrigger>
            <TabsTrigger value="manual" data-testid="tab-manual" className="rounded-full data-[state=active]:bg-cyan-500 data-[state=active]:text-black">Manual Clips</TabsTrigger>
            <TabsTrigger value="studio" data-testid="tab-studio" className="rounded-full data-[state=active]:bg-blue-600 data-[state=active]:text-white">Creator Studio</TabsTrigger>
            <TabsTrigger value="clips" data-testid="tab-clips" className="rounded-full data-[state=active]:bg-cyan-500 data-[state=active]:text-black">My clips</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates" className="rounded-full data-[state=active]:bg-cyan-500 data-[state=active]:text-black">Templates</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans" className="rounded-full data-[state=active]:bg-cyan-500 data-[state=active]:text-black">Plans</TabsTrigger>
            <TabsTrigger value="account" data-testid="tab-account" className="rounded-full data-[state=active]:bg-cyan-500 data-[state=active]:text-black">Account</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Film} label="Clips created" value={stats?.total_clips ?? "—"} accent="bg-cyan-500/10 text-cyan-400" />
              <StatCard icon={Layers} label="Projects" value={stats?.total_projects ?? "—"} accent="bg-fuchsia-500/10 text-fuchsia-400" />
              <StatCard icon={Flame} label="Avg virality" value={stats ? `${stats.avg_virality}` : "—"} accent="bg-yellow-500/10 text-yellow-400" />
              <StatCard icon={Zap} label={`${stats?.plan_name || "Free"} · left this month`} value={stats?.remaining ?? "—"} accent="bg-indigo-500/10 text-indigo-400" />
            </div>
            <div className="rounded-2xl border border-surface-800 bg-panel p-6">
              <h3 className="font-display text-lg font-semibold mb-1">Recent clips</h3>
              <p className="text-sm text-copy-500 mb-5">Your latest generated shorts.</p>
              {clips.length === 0 ? (
                <div className="text-center py-10">
                  <Clapperboard className="h-10 w-10 text-copy-700 mx-auto mb-3" />
                  <p className="text-copy-500">No clips yet. Head to “Create clips” to make your first batch.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {clips.slice(0, 5).map((c, i) => <ClipCard key={c.id} clip={c} index={i} editable onEdited={refresh} />)}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="create">
            <div className="max-w-3xl mx-auto rounded-3xl border border-surface-800 bg-upload p-6 sm:p-8">
              <h2 className="font-display text-2xl font-bold mb-1">New project</h2>
              <p className="text-sm text-copy-500 mb-6">Upload a long-form video and pick a style template.</p>
              <UploadZone onUploaded={(id) => navigate(`/processing/${id}`)} />
            </div>
          </TabsContent>

          <TabsContent value="assistant">
            <div data-testid="dashboard-clip-assistant" className="h-[780px] max-h-[calc(100dvh-10rem)] min-h-[520px] overflow-hidden rounded-2xl border border-surface-800">
              <ClipAssistantPanel />
            </div>
          </TabsContent>

          <TabsContent value="manual"><ManualWorkspace key={user.id} userId={user.id} remaining={stats?.remaining} onBusyChange={setManualBusy} onExportsChanged={refresh} /></TabsContent>
          <TabsContent value="studio"><CreatorWorkspace key={user.id} userId={user.id} remaining={stats?.remaining} onBusyChange={setStudioBusy} onExportsChanged={refresh} /></TabsContent>
          <TabsContent value="clips">
            {projects.length > 0 && <div data-testid="dashboard-batch-downloads" className="mb-8 flex flex-wrap items-start gap-4 border-b border-surface-800 pb-6">
              <div className="w-full sm:max-w-sm space-y-2"><label htmlFor="batch-project" className="text-sm font-medium">Download by source video</label><select id="batch-project" data-testid="dashboard-batch-project" aria-label="Source video for ZIP download" value={downloadProject} onChange={(event) => setDownloadProject(event.target.value)} className="w-full min-w-0 rounded-xl border border-surface-700 bg-surface-950 p-3 text-sm text-foreground"><option value="">Choose a source video</option>{projects.map((project) => <option key={project.id} value={project.id}>{`${project.filename} · ${project.num_clips} ready`}</option>)}</select></div>
              {selectedProject && <BatchDownload key={selectedProject.id} projectId={selectedProject.id} readyCount={selectedProject.num_clips} />}
            </div>}
            {clips.length === 0 ? (
              <div className="text-center py-20">
                <Clapperboard className="h-12 w-12 text-copy-700 mx-auto mb-4" />
                <p className="text-copy-500">You haven't generated any clips yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="all-clips-grid">
                {clips.map((c, i) => <ClipCard key={c.id} clip={c} index={i} editable onEdited={refresh} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="templates">
            <TemplateManager />
          </TabsContent>

          <TabsContent value="plans">
            <Billing stats={stats} onRefresh={refresh} />
          </TabsContent>

          <TabsContent value="account">
            <AccountSettings user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
